import { DecisionTool } from "../../generated/backend/v1/plan";

/**
 * The Decisions tools: titles, descriptions, and routes, for the hub and for AbiChat (its
 * simulation tools and the pages it can open, assistant/pages.ts).
 */
export type ToolSlug = "expense" | "home" | "job" | "car" | "financed-purchase" | "insurance";

export type ToolInfo = {
  tool: DecisionTool;
  slug: ToolSlug;
  title: string;
  description: string;
  href: string;
  icon: string;
  /** The card's tint on the hub (v38's `.hubc.<color>`). */
  color: "gold" | "teal" | "mag" | "amber" | "rose" | "blue";
  /** The line under the module's title on its page (v47's `lede`, without the demo household's figures). */
  lede: string;
  /** What the answer side says before Analyze is pressed (v43's `decWaiting`). */
  waiting: string;
  /** A step-by-step module (v40's `wizard`): it lays out its own steps and puts the answer in them. */
  wizard?: boolean;
};

/**
 * In v40's order (Shayan, 2026-10-29): income first, because it is the thing most likely to change
 * under you, then the two big purchases, then the smaller ones, then protection.
 */
export const TOOLS: ToolInfo[] = [
  {
    tool: DecisionTool.JOB,
    slug: "job",
    title: "What happens if my income changes?",
    description: "A raise, a cut, a new job, or a gap between jobs",
    href: "/app/decisions/job",
    icon: "💼",
    color: "mag",
    lede: "A new salary changes what lands each month, and a gap between jobs changes what you need to get there. Abi re-runs your whole plan with it, tax and all.",
    waiting:
      "Set the new salary and when it starts, then analyze it against the plan you have today.",
  },
  {
    tool: DecisionTool.HOME,
    slug: "home",
    title: "Can I afford this mortgage?",
    description: "Price, down payment and the payment, against your whole plan",
    href: "/app/decisions/home",
    icon: "🏠",
    color: "teal",
    lede: "The question is whether the payment leaves your plan standing. Abi re-runs your whole plan with the home, the mortgage, and the down payment in it.",
    waiting:
      "Set the price, the down payment and the rate, then analyze it against the plan you have today.",
  },
  {
    tool: DecisionTool.CAR,
    slug: "car",
    title: "Can I purchase a car?",
    description: "All in: financing, insurance, fuel and upkeep",
    href: "/app/decisions/car",
    icon: "🚗",
    color: "amber",
    lede: "Cars are the decision people get wrong most often, because the payment is visible and the running costs are easy to forget. Put both in and Abi re-runs your plan with them.",
    waiting: "Set the car you are weighing up, then analyze it against the plan you have today.",
  },
  {
    tool: DecisionTool.EXPENSE,
    slug: "expense",
    title: "What happens if I add this expense",
    description: "What something new every month does to the goals it competes with",
    href: "/app/decisions/expense",
    icon: "💰",
    color: "gold",
    lede: "Anything that lands every month competes with your goals for the same money. Put the amount in and Abi re-runs the plan around it.",
    waiting:
      "Set what it is, how much and how often, then analyze it against the plan you have today.",
  },
  {
    tool: DecisionTool.FINANCED_PURCHASE,
    slug: "financed-purchase",
    title: "Can I afford this financed expense?",
    description: "The number the seller quotes, against the one it costs you",
    href: "/app/decisions/financed-purchase",
    icon: "💳",
    color: "rose",
    lede: "Any purchase you borrow for has two numbers: the one the seller quotes and the one it costs you. Abi works out the second.",
    waiting:
      "Set what it costs, the rate and the term, then analyze it against the plan you have today.",
  },
  {
    tool: DecisionTool.INSURANCE,
    slug: "insurance",
    title: "Insurance analysis",
    description: "What happens to the plan if an income stops, and what closing the gap is worth",
    href: "/app/decisions/insurance",
    icon: "🛡️",
    color: "blue",
    lede: "What happens to your plan if an income stops. Not whether the premium is cheap — whether your family still reaches the goals in this plan on the day you need it.",
    waiting:
      "Set who, what event and how much coverage to test, then analyze it. Abi re-runs your whole plan with the event in it and the coverage paying out on the day.",
    wizard: true,
  },
];

/**
 * Tools still in development, shown on the hub as "Coming soon". They have no route, no
 * `DecisionTool` value, and no AbiChat tool, so AbiChat never suggests them.
 * "What should I do with extra money?" is v39's; "What if I changed my budget?" is a working module
 * in v41, held back here (Ian, 2026-11-01) because it needs the transaction feed and stored budgets.
 */
export const COMING_SOON: { title: string; description: string; icon: string }[] = [
  {
    title: "What should I do with extra money?",
    description:
      "Put a bonus, tax refund or inheritance where it does the most good against your priorities",
    icon: "🎁",
  },
  {
    title: "What if I changed my budget?",
    description:
      "Try a budget against the plan before you commit to it, then adopt it in one press",
    icon: "📊",
  },
];

export function toolBySlug(slug: string | undefined): ToolInfo | undefined {
  return TOOLS.find((t) => t.slug === slug);
}

export function toolByEnum(tool: DecisionTool): ToolInfo | undefined {
  return TOOLS.find((t) => t.tool === tool);
}

/** How long a scenario is kept (DEVIATIONS.md "Lifecycle of stored data"). */
export const SCENARIO_TTL_DAYS = 7;
